import { useState } from 'react'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/shadcn/ui/dialog'
import { Spinner } from '~/shadcn/ui/spinner'
import { LabelValueList } from '~/@mid_ui/label-value/LabelValueList'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { HeadingThree } from '~/@mid_ui/typography/HeadingThree'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { useUser } from '~/@biz_account/useUser/useUser'
import { CompanyRegistrationForm } from '~/@pages_onboarding/company-confirmation/_layout/CompanyConfirmationForm'
import { useCompanyProfile } from './useCompanyProfile'
import { getCompanyInfoItems } from './companyInfoItems'
import { Button } from '~/shadcn/ui/button/button'
import { useCountryName } from '~/@mid_settings/countries/useCountryName'

type Props = {
  hasUserSignedContract: boolean
}

function CompanyInfoInner({ hasUserSignedContract }: Props) {
  const { setNotification } = useLocalNotifications()
  const { user, reloadUser } = useUser()
  const {
    setIsCompanyMutation,
    isRequestingSetIsCompany,
    updateCompanyDataMutation,
    isRequestingUpdateCompanyData,
    updateCompanyDataError,
  } = useCompanyProfile()
  const { getCountryName } = useCountryName()

  const [isCompanyProfileDialogShown, setIsCompanyProfileDialogShown] =
    useState(false)

  const companyInfoItems = (() =>
    user != null
      ? getCompanyInfoItems(
          user.profile,
          getCountryName(user?.profile.address?.state ?? '')
        )
      : [])()

  const canSetCompanyInfo = (() => {
    return !hasUserSignedContract || !user?.profile?.userConfirmedCompanyInfo
  })()

  const companyFormTriggerButton = (() => {
    const label = user?.profile?.company?.name
      ? 'Upraviť údaje o firme'
      : 'Doplniť údaje o firme'
    return (
      <Button
        variant="outline"
        type="button"
        onClick={() => {
          setIsCompanyProfileDialogShown(true)
        }}>
        {label}
      </Button>
    )
  })()

  function handleSetIsCompany(isCompany: boolean) {
    setIsCompanyMutation(
      { isCompany },
      {
        onSuccess: reloadUser,
        onError: (error) => {
          setNotification(
            'error',
            getApiErrorMessage(error, 'Typ účtu sa nepodarilo zmeniť.')
          )
        },
      }
    )
  }

  return (
    <div className="flex flex-col flex-grow">
      <div className="w-full py-10 border-t lg:py-10 flex flex-col last:flex-grow">
        <PageContainer className="flex flex-col gap-10 md:pl-40 md:pr-15">
          <HeadingThree>Údaje o firme/živnostníkovi</HeadingThree>

          {user?.profile?.isCompany && (
            <>
              {user?.profile.company?.name != null && (
                <LabelValueList
                  items={companyInfoItems}
                  itemClassName="flex flex-col lg:flex-row lg:gap-3 justify-start"
                />
              )}

              {canSetCompanyInfo && (
                <div className="flex flex-col lg:flex-row gap-2">
                  {companyFormTriggerButton}

                  <Button
                    variant="ghost"
                    type="button"
                    className="text-black"
                    disabled={isRequestingSetIsCompany}
                    onClick={() => {
                      handleSetIsCompany(false)
                    }}>
                    Prepnúť na osobný profil{` `}
                    {isRequestingSetIsCompany && <Spinner className="size-4" />}
                  </Button>
                </div>
              )}
            </>
          )}
          {isCompanyProfileDialogShown && (
            <Dialog
              open={isCompanyProfileDialogShown}
              onOpenChange={setIsCompanyProfileDialogShown}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle title="Údaje o firme/živnostníkovi">
                    Údaje o firme/živnostníkovi
                  </DialogTitle>
                </DialogHeader>
                <CompanyRegistrationForm
                  defaultValues={user?.profile?.company}
                  onSubmit={(data) => {
                    updateCompanyDataMutation(
                      { company: data },
                      {
                        onSuccess: () => {
                          setIsCompanyProfileDialogShown(false)
                          reloadUser()
                        },
                      }
                    )
                  }}
                  isRequesting={isRequestingUpdateCompanyData}
                  error={updateCompanyDataError}
                />
              </DialogContent>
            </Dialog>
          )}

          {/* if not company, hasnt filled questionnaire and hasnt signed contract allow to switch to company */}
          {!user?.profile?.isCompany && !hasUserSignedContract && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                type="button"
                disabled={isRequestingSetIsCompany}
                onClick={() => {
                  handleSetIsCompany(true)
                }}>
                Prepnúť na firemný/podnikateľský profil{` `}
                {isRequestingSetIsCompany && <Spinner className="size-4" />}
              </Button>
            </div>
          )}
        </PageContainer>
      </div>
    </div>
  )
}

export { CompanyInfoInner }
